import { useEffect, useMemo, useState } from "react";

import { ClientResponseError } from "@/Common/Api/ClientResponse";
import { Guid } from "@/Common/Models/Guid";
import { navigateToFlowsheetFromSummariesAction } from "../Actions/Actions";
import { FlowsheetClient } from "../Api/FlowsheetClient";
import { FlowsheetLoadMedicalItemData } from "../Helpers/FlowsheetLoadMedicalItemData";
import getFlowsheetResponseToFlowsheet from "../Mappers/GetFlowsheetResponseToFlowsheetMapper";
import { CareComponent } from "../Models/CareElements/CareComponent";
import { Flowsheet } from "../Models/Flowsheet";
import { FlowsheetFilterSettings } from "../Models/FlowsheetFilterSettings";
import { FlowsheetSummary } from "../Models/FlowsheetSummary";
import { ResultsDurationType } from "../Models/ResultsDurationType";
import { useDispatch } from "./useDispatch";
import { useFlowsheet } from "./useFlowsheet";
import useFlowsheetHistory from "./useFlowsheetHistory";
import useFlowsheetStore from "./useFlowsheetStore";

interface IFlowsheetModuleProps {
    /**
     * The metadata of the flowsheet
     */
    metadata: FlowsheetSummary;

    /**
     * The ContactFacetId of the patient the flowsheet belongs to
     */
    patientId: Guid
}

interface IFlowsheetModuleResult {
    /**
     * The flowsheet data
     */
    flowsheet: Flowsheet | null;

    /**
     * The flowsheet history data
     */
    flowsheetHistory: {
        flowsheetHistory: FlowsheetSummary[];
        isLoading: boolean;
        setSearchTerm: (term: string) => void;
        searchTerm: string;
    };

    /**
     * The care component data of the flowsheet
     */
    careComponentsData: CareComponent[] | null;

    /**
     * The function to call when the care component data is updated
     */
    setCareComponentsData: (careComponentData: CareComponent[]) => void;

    /**
     * The encounter date of the flowsheet
     */
    encounterDate: Date | null;

    /**
     * The error that occurred while fetching the flowsheet
     */
    hasNotFoundError: boolean;

    /**
     * The function to call when updating the flowsheet with the template
     * @returns void
     */
    updateFlowsheetWithTemplate: () => Promise<void>;

    /**
     * The function to call when navigating to the flowsheet from summaries
     * @param flowsheetSummary - The flowsheet summary to navigate to
     * @returns void
     */
    navigateToFlowsheetFromSummaries: (flowsheetSummary: FlowsheetSummary) => void;

    /**
     * The ContactId of the patient the flowsheet belongs to
     */
    patientContactId: Guid;

    /**
     * The filter settings applied to each Care Component.
     * Its default value needs to be set based on the flowsheet configuration.
     */
    filterSettings: FlowsheetFilterSettings;

    /**
     * The function to update the filter settings
     */
    setFilterSettings: (settings: FlowsheetFilterSettings) => void;
}

function isNotFoundError(error: any): boolean {
    return error instanceof ClientResponseError
        && error.errors.some(p => p.Code === "NotFound");
}

function getFlowsheetFilterSettings(flowsheet: Flowsheet, careComponentData: CareComponent[]): FlowsheetFilterSettings {
    const careElementFilterSettings = careComponentData?.map(p => ({
        Name: p.Name,
        IsEnabled: false,
        OutsideTreatmentTarget: flowsheet.FilterSettings?.OutsideTreatmentTarget ?? false,
        OutsideTreatmentFrequency: flowsheet.FilterSettings?.OutsideTreatmentFrequency ?? false,
        ResultsDurationType: flowsheet.FilterSettings?.ResultsDurationType ?? ResultsDurationType.All,
        StartDate: flowsheet.FilterSettings?.StartDate ?? null,
        EndDate: flowsheet.FilterSettings?.EndDate ?? null,
        CareElements: p.CareElements.map(flowsheetCareElement => ({
            Key: flowsheetCareElement.Name,
            LabelKey: flowsheetCareElement.Name,
            IsEnabled: true
        }))
    }));

    const filterSetting = flowsheet?.FilterSettings ?? FlowsheetFilterSettings.createDefault(careElementFilterSettings);
    return {
        ...filterSetting,
        CareComponentFilterSettings: careElementFilterSettings
    };
}

/**
 * Custom hook to manage the flowsheet module for a patient.
 */
function useFlowsheetModule(props: Readonly<IFlowsheetModuleProps>): IFlowsheetModuleResult {
    const { saveFlowsheet, getEncounterDate, patientContactId } = useFlowsheetStore(props.patientId);

    const { data: flowsheet, error } = useFlowsheet(props.patientId, props.metadata.Id, props.metadata.Template.Id);

    const dispatch = useDispatch();

    const encounterDate = getEncounterDate(props.metadata.Id);

    const [careComponentsData, setCareComponentsData] = useState<CareComponent[] | null>(null);
    const [filterSettings, setFilterSettings] = useState<FlowsheetFilterSettings>();

    function navigateToFlowsheetFromSummaries(flowsheetSummary: FlowsheetSummary): void {
        dispatch(navigateToFlowsheetFromSummariesAction(flowsheetSummary));
    }

    useEffect(
        () => {
            if (flowsheet) {
                let flowsheetCareComponents = flowsheet.isNotApproved()
                    ? FlowsheetLoadMedicalItemData.setFlowsheetState(flowsheet.CareComponents, patientContactId, encounterDate)
                    : flowsheet.CareComponents;

                setCareComponentsData(flowsheetCareComponents);
                setFilterSettings(getFlowsheetFilterSettings(flowsheet, flowsheetCareComponents));
            }
        }
        , [flowsheet]
    );

    const handleSetCareComponentsData = (careComponentData: CareComponent[]): void => {
        setCareComponentsData(careComponentData);

        if (careComponentsData && flowsheet) {
            flowsheet.CareComponents = careComponentsData;
            saveFlowsheet(flowsheet);
        }
    };

    const handleSetFilterSettings = (settings: FlowsheetFilterSettings): void => {
        setFilterSettings(settings);

        if (settings && flowsheet) {
            flowsheet.FilterSettings = settings;
            saveFlowsheet(flowsheet);
        }
    };

    const flowsheetHistory = useFlowsheetHistory({ patientId: props.patientId });

    async function updateFlowsheetWithTemplate(): Promise<void> {
        const history = await flowsheetHistory.refetch();
        const newSummary = history.data.find(p => p.Id === props.metadata.Id);
        const getFlowsheetResponse = await FlowsheetClient.getFlowsheet(props.metadata.Id);
        const newFlowsheet = getFlowsheetResponseToFlowsheet(getFlowsheetResponse.data);
        newFlowsheet.applyPendingModificationsToComponents(careComponentsData);
        saveFlowsheet(newFlowsheet, newSummary.Template);
        navigateToFlowsheetFromSummaries(newSummary);
    }

    const hasNotFoundError = useMemo(
        () => isNotFoundError(error),
        [error]
    );

    return {
        flowsheet,
        flowsheetHistory,
        careComponentsData,
        setCareComponentsData: handleSetCareComponentsData,
        encounterDate,
        hasNotFoundError,
        updateFlowsheetWithTemplate,
        navigateToFlowsheetFromSummaries,
        patientContactId,
        filterSettings,
        setFilterSettings: handleSetFilterSettings
    };
}

export default useFlowsheetModule;
